"use client";

import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";

const StudentForm = () => {
  const { studentInfo, loginUserEmail, setStudentInfo, academicRecords } =
    useContext(AppContext);
  const [formData, setFormData] = useState({
    level: "",
    program: "",
    session: "Fall 2025",
    firstChoice: "",
    secondChoice: "",
    thirdChoice: "",
    fourthChoice: "",
    name: "",
    fatherName: "", // Father name kept separate
    gender: "",
    image: null,
    cnic: "",
    maritalStatus: "",
    mobileNo: "",
    dob: "",
    email: loginUserEmail ? loginUserEmail : "",
    religion: "",
    bloodGroup: "",
    address: "",
    country: "",
    province: "",
    city: "",
    // Father/Guardian information
    guardianName: "",
    guardianOccupation: "",
    guardianCnic: "",
    guardianMobileNo: "",
  });

  const [academicData, setAcademicData] = useState([
    {
      degree: "Matriculation OR Equivalent",
      majorSubjects: "",
      totalMarks: "",
      obtainedMarks: "",
      percentage: "",
      passingYear: "",
      boardInstitution: "",
      document: null, // Stores uploaded document
    },
    {
      degree: "Intermediate OR Equivalent",
      majorSubjects: "",
      totalMarks: "",
      obtainedMarks: "",
      percentage: "",
      passingYear: "",
      boardInstitution: "",
      document: null,
    },
    {
      degree: "MA/MSc/BS OR Equivalent",
      majorSubjects: "",
      totalMarks: "",
      obtainedMarks: "",
      percentage: "",
      passingYear: "",
      boardInstitution: "",
      document: null,
    },
  ]);

  // New state for entry test results
  const [entryTestData, setEntryTestData] = useState([
    {
      testType: "NTS/NAT",
      obtainedMarks: "",
      totalMarks: "",
      document: null,
    },
    {
      testType: "SAT",
      obtainedMarks: "",
      totalMarks: "",
      document: null,
    },
    {
      testType: "GAT",
      obtainedMarks: "",
      totalMarks: "",
      document: null,
    },
  ]);

  // Check if we're in edit mode
  const location = useLocation();
  const navigate = useNavigate();
  const isEditing = location.state?.isEditing || false;

  // Ref to track if the form has been initialized with student data
  const [formInitialized, setFormInitialized] = useState(false);

  // Initialize form with student data if in edit mode
  useEffect(() => {
    if (isEditing && studentInfo && !formInitialized) {
      // Set form data from studentInfo
      setFormData({
        level: studentInfo.level || "",
        program: studentInfo.program || "",
        session: studentInfo.session || "Fall 2025",
        firstChoice: studentInfo.firstChoice || "",
        secondChoice: studentInfo.secondChoice || "",
        thirdChoice: studentInfo.thirdChoice || "",
        fourthChoice: studentInfo.fourthChoice || "",
        name: studentInfo.name || "",
        fatherName: studentInfo.fatherName || "",
        gender: studentInfo.gender || "",
        image: null, // Can't set the file directly
        cnic: studentInfo.cnic || "",
        maritalStatus: studentInfo.maritalStatus || "",
        mobileNo: studentInfo.mobileNo || "",
        dob: studentInfo.dob
          ? new Date(studentInfo.dob).toISOString().split("T")[0]
          : "",
        email: studentInfo.email || loginUserEmail || "",
        religion: studentInfo.religion || "",
        bloodGroup: studentInfo.bloodGroup || "",
        address: studentInfo.address || "",
        country: studentInfo.country || "",
        province: studentInfo.province || "",
        city: studentInfo.city || "",
        guardianName: studentInfo.guardianName || "",
        guardianOccupation: studentInfo.guardianOccupation || "",
        guardianCnic: studentInfo.guardianCnic || "",
        guardianMobileNo: studentInfo.guardianMobileNo || "",
      });

      setFormInitialized(true);
    }
  }, [isEditing, studentInfo, formInitialized, loginUserEmail]);

  // Initialize academic data if in edit mode
  useEffect(() => {
    if (
      isEditing &&
      academicRecords &&
      academicRecords.length > 0 &&
      !formInitialized
    ) {
      const newAcademicData = [...academicData];

      // Map academic records to form structure
      academicRecords.forEach((record) => {
        const index = newAcademicData.findIndex((item) =>
          item.degree
            .toLowerCase()
            .includes(record.degree.toLowerCase().split(" ")[0])
        );

        if (index !== -1) {
          newAcademicData[index] = {
            ...newAcademicData[index],
            majorSubjects: record.majorSubjects || "",
            totalMarks: record.totalMarks || "",
            obtainedMarks: record.obtainedMarks || "",
            percentage: record.percentage || "",
            passingYear: record.passingYear || "",
            boardInstitution: record.boardInstitution || "",
            document: record.document || null,
            existingDocument: record.document || null,
          };
        }
      });

      setAcademicData(newAcademicData);
    }
  }, [isEditing, academicRecords, formInitialized]);

  // Initialize entry test data if in edit mode
  useEffect(() => {
    if (isEditing && studentInfo && !formInitialized) {
      // Fetch entry test data
      const fetchEntryTestData = async () => {
        try {
          const token = localStorage.getItem("token");
          if (!token) return;

          const response = await axios.get(
            `${process.env.REACT_APP_API_URL || ""}/students/entry-test-data`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              params: {
                email: studentInfo.email,
              },
            }
          );

          if (response.data && response.data.length > 0) {
            const newEntryTestData = [...entryTestData];

            response.data.forEach((record) => {
              const index = newEntryTestData.findIndex(
                (item) => item.testType === record.testType
              );

              if (index !== -1) {
                newEntryTestData[index] = {
                  ...newEntryTestData[index],
                  obtainedMarks: record.obtainedMarks || "",
                  totalMarks: record.totalMarks || "",
                  document: null,
                  existingDocument: record.document || null,
                };
              }
            });

            setEntryTestData(newEntryTestData);
          }
        } catch (error) {
          console.error("Error fetching entry test data:", error);
        }
      };

      fetchEntryTestData();
    }
  }, [isEditing, studentInfo, formInitialized]);

  // Store a reference for each file input
  const fileInputRefs = useRef([]);
  const entryTestFileInputRefs = useRef([]);

  // Define program options based on level
  const programOptions = {
    "Undergraduate Programs": [
      { value: "BSCS", label: "BS Computer Science" },
      { value: "BSSE", label: "BS Software Engineering" },
      { value: "BSAI", label: "BS Artificial Intelligence" },
      { value: "BSCyS", label: "BS Cyber Security" },
    ],
    Postgraduate: [
      { value: "MSAI", label: "MS Artificial Intelligence" },
      { value: "MSCS", label: "MS Computer Science" },
    ],
  };

  const [loading, setLoading] = useState(false);

  const handleAcademicChange = (index, field, value) => {
    const updatedAcademicData = [...academicData];
    updatedAcademicData[index][field] = value;
    setAcademicData(updatedAcademicData);
  };

  const handleEntryTestChange = (index, field, value) => {
    const updatedEntryTestData = [...entryTestData];
    updatedEntryTestData[index][field] = value;
    setEntryTestData(updatedEntryTestData);
  };

  const handleDocumentUpload = (index, file) => {
    const updatedAcademicData = [...academicData];
    updatedAcademicData[index].document = file;
    setAcademicData(updatedAcademicData);
  };

  const handleEntryTestDocumentUpload = (index, file) => {
    const updatedEntryTestData = [...entryTestData];
    updatedEntryTestData[index].document = file;
    setEntryTestData(updatedEntryTestData);
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formDataToSend = new FormData();

    // Append text fields from formData
    Object.keys(formData).forEach((key) => {
      formDataToSend.append(key, formData[key]);
    });

    // Filter academic data based on selected level
    const filteredAcademicData =
      formData.level === "Undergraduate Programs"
        ? academicData.slice(0, 2) // Send only first 2 fields
        : academicData; // Send all 3 fields for PostGraduate

    // Append academic data (excluding files)
    const academicDataWithoutFiles = filteredAcademicData.map(
      ({ document, existingDocument, ...rest }) => ({
        ...rest,
        document: existingDocument, // Include existing document name
      })
    );
    formDataToSend.append(
      "academicData",
      JSON.stringify(academicDataWithoutFiles)
    );

    // Append entry test data (excluding files)
    const entryTestDataWithoutFiles = entryTestData.map(
      ({ document, existingDocument, ...rest }) => ({
        ...rest,
        document: existingDocument, // Include existing document name
      })
    );
    formDataToSend.append(
      "entryTestData",
      JSON.stringify(entryTestDataWithoutFiles)
    );

    // Append file data separately
    filteredAcademicData.forEach((record, index) => {
      if (record.document && typeof record.document !== "string") {
        formDataToSend.append(`document_${index}`, record.document);
      }
    });

    // Append entry test file data separately
    entryTestData.forEach((record, index) => {
      if (record.document && typeof record.document !== "string") {
        formDataToSend.append(`entryTest_document_${index}`, record.document);
      }
    });

    // Get Bearer token
    const token = localStorage.getItem("token");

    if (!token) {
      toast.error("Authorization token missing.");
      setLoading(false);
      return; // Stop if token is not found
    }

    try {
      let response;

      if (isEditing) {
        // Update existing student
        response = await axios.put(
          `${process.env.REACT_APP_API_URL || ""}/students/update-student`,
          formDataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.status === 200) {
          toast.success("Student data updated successfully!");
          // Navigate back to the student list
          navigate("/all-students");
        }
      } else {
        // Create new student
        response = await axios.post(
          `${process.env.REACT_APP_API_URL || ""}/students/submit-student-form`,
          formDataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.status === 200 || response.status === 201) {
          toast.success("Student data submitted successfully!");
          fetchStudents(); // Reload or update student list
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Error submitting form. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    if (loginUserEmail) {
      // Get the Bearer token
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Authorization token missing.");
        return; // Stop if token is not found
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || ""}/students/student-info`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              email: loginUserEmail,
            },
          }
        );

        setStudentInfo(response.data); // Set student info
      } catch (error) {
        console.error("Error fetching student info:", error);
        toast.error("Failed to fetch student information");
      }
    }
  };

  useEffect(() => {
    const fetchStudentInfo = async () => {
      if (!loginUserEmail) return;

      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Authorization token missing.");
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || ""}/students/student-info`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              email: loginUserEmail,
            },
          }
        );
        setStudentInfo(response.data); // Set student info
      } catch (error) {
        console.error("Error fetching student info:", error);
      }
    };
    fetchStudentInfo();
  }, [loginUserEmail]);

  const handleGuardianCnicChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove non-digits
    if (value.length > 5) value = value.slice(0, 5) + "-" + value.slice(5);
    if (value.length > 13) value = value.slice(0, 13) + "-" + value.slice(13);
    if (value.length > 15) value = value.slice(0, 15); // Max length with dashes
    setFormData({ ...formData, guardianCnic: value });
  };

  const handleStudentCnicChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove non-digits
    if (value.length > 5) value = value.slice(0, 5) + "-" + value.slice(5);
    if (value.length > 13) value = value.slice(0, 13) + "-" + value.slice(13);
    if (value.length > 15) value = value.slice(0, 15); // Limit max length with dashes
    setFormData({ ...formData, cnic: value });
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
      />
      {studentInfo == null ? (
        <>
          <form
            className="max-w-7xl mx-auto p-8 bg-white shadow-lg rounded-lg "
            onSubmit={handleSubmit}
          >
            <div className="bg-blue-700 text-white p-3 rounded-t">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <img
                    src="/logo.png"
                    alt="NASTP Logo"
                    width={40}
                    height={40}
                    className="rounded-full"
                  />
                  <div>
                    <h3 className="text-2xl font-bold tracking-tight">
                      NASTP INSTITUTE OF INFORMATION TECHNOLOGY (NIIT), LAHORE
                    </h3>
                  </div>
                </div>
              </div>
            </div>

            {/* Program Applied For */}
            <div className="mb-8 mt-3">
              <h3 className="bg-blue-700 text-white font-semibold text-lg px-4 py-3 rounded-t">
                {isEditing ? "Edit Student Information" : "Program Applied For"}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-gray-100 p-6 rounded-b shadow">
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    SELECT LEVEL<span className="text-red-500">*</span>
                  </label>
                  <select
                    name="level"
                    value={formData.level}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                    required
                  >
                    <option value="">Select Level</option>
                    <option value="Undergraduate Programs">
                      Undergraduate (BS Programs)
                    </option>
                    <option value="Postgraduate">
                      Postgraduate (MS/MPhil Programs)
                    </option>
                  </select>
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    SELECT PROGRAM (First Choice)
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="program"
                    value={formData.program}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                    required
                    disabled={!formData.level}
                  >
                    <option value="">Select Program</option>
                    {formData.level &&
                      programOptions[formData.level].map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    CURRENT SESSION<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.session}
                    readOnly
                    className="w-full p-2 border rounded bg-gray-200 text-gray-600 focus:outline-none"
                  />
                </div>
                {formData.level && (
                  <div className="col-span-3 mt-4">
                    <h4 className="font-medium text-gray-700 mb-3">
                      Program Choices{" "}
                      {formData.level === "Undergraduate Programs"
                        ? "(Select up to 3)"
                        : ""}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* <div>
                        <label className="block font-medium text-gray-700 mb-2">
                          First Choice<span className="text-red-500">*</span>
                        </label>
                        <select
                          name="firstChoice"
                          value={formData.firstChoice}
                          onChange={handleInputChange}
                          className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                          required
                        >
                          <option value="">Select Program</option>
                          {formData.level &&
                            programOptions[formData.level].map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                        </select>
                      </div> */}
                      <div>
                        <label className="block font-medium text-gray-700 mb-2">
                          Second Choice
                        </label>
                        <select
                          name="secondChoice"
                          value={formData.secondChoice}
                          onChange={handleInputChange}
                          className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                        >
                          <option value="">Select Program</option>
                          {formData.level &&
                            programOptions[formData.level].map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                        </select>
                      </div>

                      {formData.level === "Undergraduate Programs" && (
                        <>
                          <div>
                            <label className="block font-medium text-gray-700 mb-2">
                              Third Choice
                            </label>
                            <select
                              name="thirdChoice"
                              value={formData.thirdChoice}
                              onChange={handleInputChange}
                              className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                            >
                              <option value="">Select Program</option>
                              {programOptions[formData.level].map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block font-medium text-gray-700 mb-2">
                              Fourth Choice
                            </label>
                            <select
                              name="fourthChoice"
                              value={formData.fourthChoice}
                              onChange={handleInputChange}
                              className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                            >
                              <option value="">Select Program</option>
                              {programOptions[formData.level].map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Student's Personal Information */}
            <div className="mb-8">
              <h3 className="bg-blue-700 text-white font-semibold text-lg px-4 py-3 rounded-t">
                Student's Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-gray-100 p-6 rounded-b shadow">
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Name<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Father Name<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fatherName"
                    value={formData.fatherName}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Gender<span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="gender"
                        value="Male"
                        onChange={handleInputChange}
                        required
                        className="mr-2 focus:ring focus:ring-blue-500"
                      />
                      Male
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="gender"
                        value="Female"
                        onChange={handleInputChange}
                        required
                        className="mr-2 focus:ring focus:ring-blue-500"
                      />
                      Female
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Image<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    name="image"
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:outline-none"
                    accept="image/jpeg, image/png"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    CNIC<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="cnic"
                    value={formData.cnic}
                    onChange={handleStudentCnicChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                    placeholder="XXXXX-XXXXXXX-X"
                    maxLength={15}
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Marital Status
                  </label>
                  <select
                    name="maritalStatus"
                    value={formData.maritalStatus}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="">Select Status</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                  </select>
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Mobile No.<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="mobileNo"
                    value={formData.mobileNo}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                    placeholder="03XXXXXXX"
                    maxLength={11}
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Date of Birth<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Email<span className="text-red-500">*</span>
                  </label>
                  <input
                    readOnly
                    type="email"
                    name="email"
                    value={loginUserEmail}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Religion<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="religion"
                    value={formData.religion}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Blood Group<span className="text-red-500">*</span>
                  </label>
                  <select
                    name="bloodGroup"
                    value={formData.bloodGroup}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="" disabled>
                      Select Blood Group
                    </option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Address & Guardian Information */}
            <div>
              <h3 className="bg-blue-700 text-white font-semibold text-lg px-4 py-3 rounded-t">
                Address & Guardian Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-gray-100 p-6 rounded-b shadow">
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Address<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Country<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Province<span className="text-red-500">*</span>
                  </label>
                  <select
                    name="province"
                    value={formData.province}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                    required
                  >
                    <option value="">Select Province</option>
                    <option value="Punjab">Punjab</option>
                    <option value="Sindh">Sindh</option>
                    <option value="KPK">KPK</option>
                    <option value="Baluchistan">Baluchistan</option>
                    <option value="ICT (Islamabad)">ICT (Islamabad)</option>
                    <option value="Azad Kashmir">Azad Kashmir</option>
                    <option value="Gilgit-Baltistan">Gilgit-Baltistan</option>
                  </select>
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    City<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Guardian Name<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="guardianName"
                    value={formData.guardianName}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Guardian Occupation<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="guardianOccupation"
                    value={formData.guardianOccupation}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Guardian CNIC<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="guardianCnic"
                    value={formData.guardianCnic}
                    onChange={handleGuardianCnicChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                    placeholder="XXXXX-XXXXXXX-X"
                    required
                    maxLength={15}
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Guardian Mobile No.<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="guardianMobileNo"
                    value={formData.guardianMobileNo}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring focus:ring-blue-500 focus:outline-none"
                    placeholder="03XXXXXXX"
                    maxLength={11}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Academic Background Section */}
            <div className="mb-8 mt-8">
              <h3 className="bg-blue-700 text-white font-semibold text-lg px-4 py-3 rounded-t">
                Academic Background (including Short Courses and
                Admission/Aptitude Test Certificates)
              </h3>
              <div className="bg-gray-100 p-6 rounded-b shadow overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300 text-sm text-gray-700">
                  <thead>
                    <tr className="bg-gray-200 text-left">
                      <th className="border border-gray-300 p-2">
                        Degree/Certificate
                      </th>
                      <th className="border border-gray-300 p-2">
                        Major Subjects<span className="text-red-500">*</span>
                      </th>
                      <th className="border border-gray-300 p-2">
                        Total Marks/CGPA<span className="text-red-500">*</span>
                      </th>
                      <th className="border border-gray-300 p-2">
                        Obtained Marks/CGPA
                        <span className="text-red-500">*</span>
                      </th>
                      <th className="border border-gray-300 p-2">
                        Percentage<span className="text-red-500">*</span>
                      </th>
                      <th className="border border-gray-300 p-2">
                        Passing Year<span className="text-red-500">*</span>
                      </th>
                      <th className="border border-gray-300 p-2">
                        Board/University Institution
                        <span className="text-red-500">*</span>
                      </th>
                      <th className="border border-gray-300 p-2">
                        Documents Upload<span className="text-red-500">*</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {academicData
                      .slice(
                        0,
                        formData.level === "Undergraduate Programs" ? 2 : 3
                      ) // Dynamically show fields based on level
                      .map((row, index) => (
                        <tr key={index}>
                          <td className="border border-gray-300 p-2">
                            {row.degree}
                          </td>
                          <td className="border border-gray-300 p-2">
                            <input
                              type="text"
                              value={row.majorSubjects}
                              onInput={(e) => {
                                const inputValue = e.target.value;
                                handleAcademicChange(
                                  index,
                                  "majorSubjects",
                                  inputValue
                                );
                              }}
                              className="w-full p-1 border rounded"
                              required
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <input
                              type="number"
                              value={row.totalMarks}
                              onChange={(e) =>
                                handleAcademicChange(
                                  index,
                                  "totalMarks",
                                  e.target.value
                                )
                              }
                              className="w-full p-1 border rounded"
                              required
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <input
                              type="number"
                              value={row.obtainedMarks}
                              onChange={(e) => {
                                const newObtainedMarks = e.target.value;

                                // Only check if the input is a valid number and
                                if (
                                  newObtainedMarks !== "" &&
                                  !isNaN(newObtainedMarks)
                                ) {
                                  if (
                                    Number.parseFloat(newObtainedMarks) <=
                                    row.totalMarks
                                  ) {
                                    handleAcademicChange(
                                      index,
                                      "obtainedMarks",
                                      newObtainedMarks
                                    );
                                  } else {
                                    toast.error(
                                      "Obtained marks cannot be greater than total marks"
                                    );
                                  }
                                } else {
                                  // If empty or invalid input, just update the value (no alert)
                                  handleAcademicChange(
                                    index,
                                    "obtainedMarks",
                                    newObtainedMarks
                                  );
                                }
                              }}
                              className="w-full p-1 border rounded"
                              required
                            />
                          </td>

                          <td className="border border-gray-300 p-2">
                            <input
                              type="number"
                              value={row.percentage}
                              onChange={(e) =>
                                handleAcademicChange(
                                  index,
                                  "percentage",
                                  e.target.value
                                )
                              }
                              className="w-full p-1 border rounded"
                              required
                              placeholder="92.3"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <input
                              type="number"
                              value={row.passingYear}
                              onChange={(e) =>
                                handleAcademicChange(
                                  index,
                                  "passingYear",
                                  e.target.value
                                )
                              }
                              className="w-full p-1 border rounded"
                              required
                              placeholder="2024"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <input
                              value={row.boardInstitution}
                              onInput={(e) => {
                                const inputValue = e.target.value;
                                handleAcademicChange(
                                  index,
                                  "boardInstitution",
                                  inputValue
                                );
                              }}
                              className="w-full p-1 border rounded"
                              required
                            />
                          </td>
                          <td className="border border-gray-300 p-2 text-center">
                            <input
                              type="file"
                              ref={(el) => (fileInputRefs.current[index] = el)}
                              className="hidden"
                              onChange={(e) =>
                                handleDocumentUpload(index, e.target.files[0])
                              }
                              required
                              accept=".pdf,.jpg,.png,.jpeg"
                            />
                            {row.document ? (
                              <button
                                type="button"
                                className="text-blue-600 hover:underline"
                                onClick={() =>
                                  fileInputRefs.current[index]?.click()
                                }
                                required
                              >
                                {row.document.name}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="bg-blue-500 text-white px-3 py-1 rounded"
                                onClick={() =>
                                  fileInputRefs.current[index]?.click()
                                }
                                required
                              >
                                Upload
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>

                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-gray-700 flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-yellow-500 mr-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <strong>Note:</strong> If any degree consists of more than
                    one page, generate its PDF and upload it as an attachment.
                  </p>
                </div>
              </div>
            </div>

            {/* Entry Test Result Section */}
            <div className="mb-8 mt-8">
              <h3 className="bg-blue-700 text-white font-semibold text-lg px-4 py-3 rounded-t">
                Entry Test Result
              </h3>
              <div className="bg-gray-100 p-6 rounded-b shadow overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300 text-sm text-gray-700">
                  <thead>
                    <tr className="bg-gray-200 text-left">
                      <th className="border border-gray-300 p-2">Test Type</th>
                      <th className="border border-gray-300 p-2">
                        Marks Obtained
                      </th>
                      <th className="border border-gray-300 p-2">
                        Total Marks
                      </th>
                      <th className="border border-gray-300 p-2">
                        Upload Document
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {entryTestData
                      .filter((row) =>
                        formData.level === "Undergraduate Programs"
                          ? row.testType === "NTS/NAT" || row.testType === "SAT"
                          : row.testType === "GAT"
                      )
                      .map((row, index) => (
                        <tr key={index}>
                          <td className="border border-gray-300 p-2">
                            {row.testType}
                          </td>
                          <td className="border border-gray-300 p-2">
                            <input
                              type="number"
                              value={row.obtainedMarks}
                              onChange={(e) => {
                                const newObtainedMarks = e.target.value;

                                // Only check if the input is a valid number and total marks is provided
                                if (
                                  newObtainedMarks !== "" &&
                                  !isNaN(newObtainedMarks) &&
                                  row.totalMarks !== ""
                                ) {
                                  if (
                                    Number.parseFloat(newObtainedMarks) <=
                                    Number.parseFloat(row.totalMarks)
                                  ) {
                                    handleEntryTestChange(
                                      entryTestData.findIndex(
                                        (item) => item.testType === row.testType
                                      ),
                                      "obtainedMarks",
                                      newObtainedMarks
                                    );
                                  } else {
                                    toast.error(
                                      "Obtained marks cannot be greater than total marks"
                                    );
                                  }
                                } else {
                                  // If empty or invalid input, just update the value (no alert)
                                  handleEntryTestChange(
                                    entryTestData.findIndex(
                                      (item) => item.testType === row.testType
                                    ),
                                    "obtainedMarks",
                                    newObtainedMarks
                                  );
                                }
                              }}
                              className="w-full p-1 border rounded"
                              placeholder="Enter marks"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <input
                              type="number"
                              value={row.totalMarks}
                              onChange={(e) =>
                                handleEntryTestChange(
                                  entryTestData.findIndex(
                                    (item) => item.testType === row.testType
                                  ),
                                  "totalMarks",
                                  e.target.value
                                )
                              }
                              className="w-full p-1 border rounded"
                              placeholder="Enter total marks"
                            />
                          </td>
                          <td className="border border-gray-300 p-2 text-center">
                            <input
                              type="file"
                              ref={(el) =>
                                (entryTestFileInputRefs.current[
                                  entryTestData.findIndex(
                                    (item) => item.testType === row.testType
                                  )
                                ] = el)
                              }
                              className="hidden"
                              onChange={(e) =>
                                handleEntryTestDocumentUpload(
                                  entryTestData.findIndex(
                                    (item) => item.testType === row.testType
                                  ),
                                  e.target.files[0]
                                )
                              }
                              accept=".pdf,.jpg,.png,.jpeg"
                            />
                            {row.document ? (
                              <button
                                type="button"
                                className="text-blue-600 hover:underline"
                                onClick={() =>
                                  entryTestFileInputRefs.current[
                                    entryTestData.findIndex(
                                      (item) => item.testType === row.testType
                                    )
                                  ]?.click()
                                }
                              >
                                {row.document.name}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="bg-blue-500 text-white px-3 py-1 rounded"
                                onClick={() =>
                                  entryTestFileInputRefs.current[
                                    entryTestData.findIndex(
                                      (item) => item.testType === row.testType
                                    )
                                  ]?.click()
                                }
                              >
                                Upload
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>

                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-gray-700 flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-yellow-500 mr-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <strong>Note:</strong> Entry test results are optional. You
                    can leave them empty if not applicable.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-700 text-white px-6 py-2 mt-6 rounded shadow hover:bg-blue-800 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {isEditing ? "Updating..." : "Submitting..."}
                  </>
                ) : isEditing ? (
                  "Update"
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </form>
        </>
      ) : (
        <>
          <div className="max-w-7xl mx-auto p-6">
            {/* Header Section */}
            <div className="bg-blue-800 text-white text-lg font-semibold px-4 py-3 rounded-t">
              Form Status
            </div>

            {/* Content Section */}
            <div className="border border-gray-300 p-6 rounded-b bg-white shadow-md">
              <p className="text-center text-lg">
                <span className="font-bold text-black">Congratulations!</span>{" "}
                Your form is submitted.
              </p>
            </div>
          </div>
          {/* <div className="max-w-7xl mx-auto bg-white shadow-md rounded-lg p-6"></div> */}
        </>
      )}
    </>
  );
};

export default StudentForm;
